import { GenderOptions } from "app/constants/residentDetailsConstants";
import { ResidentInputFields } from "app/services/api/types/residents/models/ResidentInputFields";
import { useMemo } from "react";
import {
  alphabetsValidation,
  alphaNumericValidationWithSpace,
  numberValidation,
  requiredValidation,
} from "ui/utils/Validators";
import { useGetCountriesQuery } from "app/services/api/countriesApi";

interface Props {
  allowMaritalDescription?: boolean;
}
export const useCreateResidentFields = ({ allowMaritalDescription }: Props) => {
  const { data: countriesData } = useGetCountriesQuery(undefined);

  const inputFields = useMemo<ResidentInputFields[]>(() => {
    return [
      {
        id: "resident_id",
        name: "resident_id",
        label: "Resident ID",
        placeholder: "Enter resident ID",
        isRequired: false,
        rules: {
          validate: numberValidation,
        },
      },
      {
        id: "first_name",
        name: "first_name",
        label: "First name",
        placeholder: "Enter resident first name",
        isRequired: true,
        rules: {
          ...requiredValidation("First name"),
          validate: alphabetsValidation,
        },
      },
      {
        id: "last_name",
        name: "last_name",
        label: "Last name",
        placeholder: "Enter resident last name",
        isRequired: true,
        rules: {
          ...requiredValidation("Last name"),
          validate: alphabetsValidation,
        },
      },
      {
        id: "known_as",
        name: "known_as",
        label: "Known As",
        placeholder: "Known as",
        isRequired: false,
      },
      {
        id: "identification_type",
        name: "identification_type",
        label: "Identification Type",
        placeholder: "Select identification type",
        isRequired: true,
        rules: {
          ...requiredValidation("Identification Type"),
        },
        inputType: "dropdown",
        options: [
          { value: "id_card_number", label: "ID Card Number" },
          { value: "passport_number", label: "Passport Number" },
        ],
      },
      {
        id: "identification_number",
        name: "identification_number",
        label: "ID/Passport Number",
        placeholder: "Enter ID/passport number",
        isRequired: true,
        rules: {
          ...requiredValidation("ID/Passport Number"),
          validate: alphaNumericValidationWithSpace,
        },
      },
      {
        id: "gender",
        name: "gender",
        label: "Gender",
        placeholder: "Select gender",
        isRequired: true,
        rules: {
          ...requiredValidation("Gender"),
        },
        inputType: "dropdown",
        options: GenderOptions,
      },
      {
        id: "date_of_birth",
        name: "date_of_birth",
        label: "Date of Birth",
        placeholder: "(DD-MM-YYYY)",
        inputType: "date",
        isRequired: true,
        rules: {
          ...requiredValidation("Date of Birth"),
        },
      },
      {
        id: "nationality_id",
        name: "nationality_id",
        label: "Nationality",
        placeholder: "Select nationality",
        isRequired: false,
        inputType: "dropdown",
        options:
          countriesData?.map((country) => ({
            value: country.id,
            label: country.nationality ?? country.name,
          })) || [],
      },
      {
        id: "marital_status",
        name: "marital_status",
        label: "Marital Status",
        placeholder: "Select",
        isRequired: false,
        inputType: "dropdown",
        options: [
          { value: "married", label: "Married" },
          { value: "single", label: "Single" },
          { value: "separated", label: "Separated" },
          { value: "divorced", label: "Divorced" },
          { value: "widow", label: "Widow" },
          { value: "other", label: "Other" },
        ],
      },
      {
        id: "marital_status_description",
        name: "marital_status_description",
        label: "Marital Status Description",
        placeholder: "Enter details if marital status is Other",
        isRequired: false,
        disabled: !allowMaritalDescription,
      },
      {
        id: "religion",
        name: "religion",
        label: "Religion",
        placeholder: "Enter religion",
        isRequired: false,
      },
      {
        id: "former_occupation",
        name: "former_occupation",
        label: "Former Occupation",
        placeholder: "Enter former occupation",
        isRequired: false,
      },
      {
        id: "location_id",
        name: "location_id",
        label: "Home allocation",
        placeholder: "Select location",
        isRequired: false,
        inputType: "location",
      },
      {
        id: "outside_pass_status",
        name: "outside_pass_status",
        label: "Outside Pass Status",
        placeholder: "Select option",
        isRequired: true,
        inputType: "dropdown",
        options: [
          { value: "independent", label: "Independant" },
          { value: "accompanied", label: "Accompanied" },
          { value: "denied", label: "Denied" },
          { value: "other", label: "Other" },
        ],
      },
    ];
  }, [countriesData, allowMaritalDescription]);

  return { inputFields };
};
